import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Button, Tooltip, Space, Input, message, Spin, Tag } from 'antd';

import { DeleteOutlined, PlusOutlined, CheckOutlined, RollbackOutlined } from '@ant-design/icons';
import TableWithSearch from '../../components/TableWithSearch';
import { ColumnsType } from 'antd/es/table';
import { usePostClickTracking } from '@src/api/emp/queryHooks';
import moment from 'moment';
import DefaultAttributes from '../DefaultAttributes';

const { Text } = Typography;

const CollectAttributes = ({
  collectAttributesData,
  collectAttributesMetadata,
  refetchClickTracking,
  setUnsavedChangesForAttributes,
  isLoadingClickTrackingData,
}: {
  collectAttributesData: any[];
  collectAttributesMetadata: any;
  refetchClickTracking: () => void;
  setUnsavedChangesForAttributes: React.Dispatch<React.SetStateAction<number>>;
  isLoadingClickTrackingData: boolean;
}) => {
  const { mutate: createClickTracking, isLoading: isLoadingCreateClickTracking } =
    usePostClickTracking();

  const isLoading = useMemo(
    () => isLoadingCreateClickTracking || isLoadingClickTrackingData,
    [isLoadingCreateClickTracking, isLoadingClickTrackingData],
  );

  const [newAttributes, setNewAttributes] = useState<string[]>([]);

  const handleAddInput = () => {
    setNewAttributes([...newAttributes, '']);
    setIsDirty(true);
  };

  const handleInputChange = (index: number, value: string) => {
    const updated = [...newAttributes];
    updated[index] = value;
    setNewAttributes(updated);
    setIsDirty(true);
  };

  const evaluateIsDirty = (inputs: string[], deletedSet: Set<string>) => {
    return !(inputs.length === 0 && deletedSet.size === 0);
  };

  const handleMarkDeleted = (index: number) => {
    const updated = newAttributes.filter((_, i) => i !== index);
    setNewAttributes(updated);
    setIsDirty(evaluateIsDirty(updated, deletedRows));
  };

  const handleSaveInputs = () => {
    const existingAttributes = (collectAttributesData || []).map((attr) => attr.trim());
    const activeInputs = existingAttributes.filter((item) => !deletedRows.has(item));
    const newCleaned = (newAttributes || []).map((v) => v.trim());

    const hasEmpty = newCleaned.some((v) => v === '');
    if (hasEmpty) {
      message.error('Please enter a name for each newly added Custom Attribute row.');
      return;
    }
    const allToSave = [...activeInputs, ...newCleaned];

    const hasDuplicates = new Set(allToSave).size !== allToSave.length;

    if (hasDuplicates) {
      message.error(
        'Duplicate attribute names found. Please ensure all attribute names are unique (case-insensitive, trimmed).',
      );
      return;
    }

    createClickTracking(
      { queryBody: { en: true, collectAttr: allToSave } },
      {
        onSuccess: () => {
          refetchClickTracking();
          message.success({
            content: (
              <div style={{ textAlign: 'left' }}>
                Changes to the configuration have been saved successfully.
                <br />
                Sensors may take up to 5 minutes to reflect changes.
              </div>
            ),
            duration: 5,
          });
          setNewAttributes([]);
          setDeletedRows(new Set());

          setIsDirty(false);
        },
        onError: (error) => {
          const errorMsg =
            error?.response?.data?.msg ||
            error?.msg ||
            error?.message ||
            'An unexpected error occurred.';
          message.error(errorMsg);
        },
      },
    );
  };

  const [deletedRows, setDeletedRows] = useState<Set<string>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const unsavedCount = newAttributes.length + deletedRows.size;
  const tableData = useMemo(() => {
    if (!collectAttributesData) return [];
    return collectAttributesData.map((attr, index) => ({
      key: index + 1,
      name: attr,
      isDeleted: deletedRows.has(attr),
    }));
  }, [collectAttributesData, deletedRows]);

  useEffect(() => {
    setUnsavedChangesForAttributes(newAttributes.length + deletedRows.size);
  }, [newAttributes, deletedRows]);

  const columns: ColumnsType<any> = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (text, record) => (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span style={{ opacity: record.isDeleted ? 0.5 : 1 }} data-test-id="attributeName">
            {text}
          </span>

          {record.isDeleted ? (
            <div>
              <Tag color="orange">Deleted - save to apply, restore to revert</Tag>
              <Tooltip title="Restore">
                <Button
                  onClick={() => {
                    setDeletedRows((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(record.name);

                      // Evaluate dirty state using the updated set
                      setIsDirty(evaluateIsDirty(newAttributes, newSet));

                      return newSet;
                    });
                  }}
                  type="text"
                  icon={<RollbackOutlined color="rgba(128, 163, 255, 1)" />}
                  data-test-id={`${record.name}-restoreAttributeButton`}
                />
              </Tooltip>
            </div>
          ) : (
            <Tooltip title="Delete">
              <Button
                onClick={() => {
                  setDeletedRows((prev) => new Set(prev).add(record.name));
                  setIsDirty(true);
                }}
                type="text"
                icon={<DeleteOutlined />}
                danger
                data-test-id={`${record.name}-deleteAttributeButton`}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={isLoading}>
      <div style={{ height: '100%', overflowY: 'auto', paddingRight: 16, position: 'relative' }}>
        <DefaultAttributes
          attributes={[
            'id',
            'class',
            'name',
            'type',
            'placeholder',
            'value',
            'a.href',
            'area.href',
            'a.target',
            'area.target',
            'xlink:href',
          ]}
          name="Default Attributes"
        />

        <div
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            backgroundColor: '#fff',
            overflow: 'visible',
          }}
        >
          <div
            style={{
              padding: '8px 12px',
              borderBottom: '1px solid #f0f0f0',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Custom Attributes
          </div>

          <div style={{ padding: 0, overflow: 'auto', maxWidth: '100%' }}>
            <TableWithSearch
              columns={columns}
              data={tableData}
              pagination={false}
              searchKeys={['name']}
              headerOffset={650}
            />
          </div>

          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #f0f0f0',
              maxHeight: newAttributes.length > 10 ? 400 : 'auto',
              overflowY: newAttributes.length > 10 ? 'auto' : 'visible',
            }}
          >
            {newAttributes.map((value, index) => {
              const trimmed = value.trim();
              const isDuplicate =
                trimmed &&
                [
                  ...collectAttributesData
                    .filter((item) => !deletedRows.has(item))
                    .map((item) => item.trim()),
                  ...newAttributes.filter((_, i) => i !== index).map((item) => item.trim()),
                ]
                  .filter(Boolean)
                  .filter((item) => item === trimmed).length > 0;

              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <Input
                    placeholder={`Input Value`}
                    value={value}
                    data-test-id="attributeInput"
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    style={{ width: 400, borderColor: isDuplicate ? 'red' : undefined }}
                    status={isDuplicate ? 'error' : ''}
                  />
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={() => handleMarkDeleted(index)}
                    type="text"
                    danger
                    style={{ marginLeft: 8 }}
                    data-test-id="deleteNewAttributeButton"
                  />
                </div>
              );
            })}
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddInput}
              type="default"
              data-test-id="addAttributeButton"
            >
              Add Attribute
            </Button>
          </div>
        </div>
        {collectAttributesMetadata?.updated_at ? (
          <div
            style={{
              marginTop: '24px',
              color: '#8c8c8c',
              fontSize: 14,
              fontWeight: 400,
            }}
            data-test-id="collectAttributesLastUpdated"
          >
            Last Updated on{' '}
            {new Date(collectAttributesMetadata?.updated_at * 1000).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}{' '}
            by {collectAttributesMetadata?.updated_by}
          </div>
        ) : null}

        {isDirty && (
          <div
            style={{
              position: 'fixed',
              left: '50%',
              bottom: 'calc(24px + env(safe-area-inset-bottom))',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              background: '#fff',
              padding: '14px 50px',
              borderRadius: 16,
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              minWidth: 420,
              maxWidth: '90vw',
            }}
            aria-live="polite"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
              }}
            >
              <span
                style={{
                  color: 'rgba(31, 31, 31, 1)',
                  fontWeight: 400,
                  fontSize: 14,
                }}
                data-test-id="unsavedChangesWarning"
              >
                You have {unsavedCount} unsaved change{unsavedCount !== 1 ? 's' : ''}
              </span>

              <Button
                type="primary"
                loading={isLoadingCreateClickTracking}
                disabled={isLoadingCreateClickTracking}
                style={{ marginLeft: 'auto' }}
                data-test-id="saveAttributesButton"
                icon={<CheckOutlined />}
                onClick={handleSaveInputs}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
};

export default CollectAttributes;
